<script>
import * as m from '@app/paraglide/messages.js';
import { fade } from "svelte/transition";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { toast } from '@zerodevx/svelte-toast';
import { BarLoader } from 'svelte-loading-spinners';
import Swal from 'sweetalert2';
import { SWAL_BASE, SWAL_CLASSES } from '@app/lib/swalClasses.js';
import { saveAs } from 'file-saver';

import {
    PRESET_TYPES,
    removePreset,
    importPresetsFromJson,
    exportPresetsAsJson,
    getPresetsByType
} from '@app/store/presetStore.svelte.js';

let activeTab = $state('subtitle'),
    selectedPreset = $state(null),
    loading = $state(false),
    presetsByType = $derived(getPresetsByType(activeTab));

const tabs = [
    { id: PRESET_TYPES.SUBTITLE, label: m.preset_manager_tab_subtitles(), icon: 'bi-badge-cc' },
    { id: PRESET_TYPES.VIDEO, label: m.preset_manager_tab_video(), icon: 'bi-camera-video' },
    { id: PRESET_TYPES.TRANSCRIPT, label: m.preset_manager_tab_transcripts(), icon: 'bi-file-text' },
    { id: PRESET_TYPES.AUDIO_DESCRIPTION, label: m.preset_manager_tab_audio_descriptions(), icon: 'bi-soundwave' },
    { id: PRESET_TYPES.EVENT_GROUP_STANDARDS, label: m.preset_manager_tab_standards(), icon: 'bi-speedometer2' }
];

function switchTab(tabId) {
    activeTab = tabId;
    selectedPreset = null;
}

function selectPreset(preset) {
    selectedPreset = preset;
}

async function deleteSelectedPreset() {
    if (!selectedPreset) return;

    const result = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.preset_manager_delete_title(),
        text: m.preset_manager_delete_text({name: selectedPreset.name}),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: m.preset_manager_delete_confirm(),
        cancelButtonText: m.preset_manager_delete_cancel(),
        customClass: SWAL_CLASSES.destructive,
    });

    if (result.isConfirmed) {
        try {
            loading = true;
            await removePreset(selectedPreset.id);
            toast.push(m.preset_manager_delete_success_toast({name: selectedPreset.name}), { classes: ['toast-success'] });
            selectedPreset = null;
        } catch (error) {
            console.error("Error deleting preset:", error);
            toast.push(m.preset_manager_delete_failed_toast({error: error.message}), { classes: ['toast-danger'] });
        } finally {
            loading = false;
        }
    }
}

function exportSelectedPreset() {
    if (!selectedPreset) return;

    try {
        const jsonString = exportPresetsAsJson([selectedPreset.id]);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const fileName = `${selectedPreset.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_preset.json`;
        saveAs(blob, fileName);
        toast.push(m.preset_manager_export_success_toast(), { classes: ['toast-success'] });
    } catch (error) {
        console.error("Error exporting preset:", error);
        toast.push(m.preset_manager_export_failed_toast({error: error.message}), { classes: ['toast-danger'] });
    }
}

function exportAllPresetsOfType() {
    const presetsOfType = getPresetsByType(activeTab);
    
    if (presetsOfType.length === 0) {
        toast.push(m.preset_manager_no_presets_toast(), { classes: ['toast-warning'] });
        return;
    }

    try {
        const presetIds = presetsOfType.map(p => p.id);
        const jsonString = exportPresetsAsJson(presetIds);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const tabLabel = tabs.find(t => t.id === activeTab)?.label || 'presets';
        const fileName = `${tabLabel.toLowerCase().replace(/\s+/g, '_')}_presets.json`;
        saveAs(blob, fileName);
        toast.push(m.preset_manager_export_all_success_toast({count: presetsOfType.length}), { classes: ['toast-success'] });
    } catch (error) {
        console.error("Error exporting presets:", error);
        toast.push(m.preset_manager_export_all_failed_toast({error: error.message}), { classes: ['toast-danger'] });
    }
}

async function importPresets() {
    const { value: file } = await Swal.fire({
        ...SWAL_BASE,
        titleText: m.preset_manager_import_title(),
        html: m.preset_manager_import_html(),
        showCancelButton: true,
        confirmButtonText: m.preset_manager_import_confirm(),
        cancelButtonText: m.preset_manager_import_cancel(),
        customClass: SWAL_CLASSES.confirmCancel,
        preConfirm: () => {
            const fileInput = document.getElementById('presetFileInput');
            if (!fileInput.files.length) {
                Swal.showValidationMessage(m.preset_manager_import_validation());
                return false;
            }
            return fileInput.files[0];
        }
    });

    if (file) {
        try {
            loading = true;
            const fileContent = await file.text();
            const results = await importPresetsFromJson(fileContent);

            let message = m.preset_manager_import_results_message({success: results.success.length, duplicates: results.duplicates.length, failed: results.failed.length});

            await Swal.fire({
                ...SWAL_BASE,
                titleText: m.preset_manager_import_results_title(),
                text: message,
                icon: results.failed.length > 0 ? 'warning' : 'success',
                customClass: SWAL_CLASSES.ok,
            });

            if (results.success.length > 0) {
                toast.push(m.preset_manager_import_success_toast({count: results.success.length}), { classes: ['toast-success'] });
            }
        } catch (error) {
            console.error("Error importing presets:", error);
            toast.push(m.preset_manager_import_failed_toast({error: error.message}), { classes: ['toast-danger'] });
        } finally {
            loading = false;
        }
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'presetManager' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="presetManagerTitle"
    aria-modal="true"
    tabindex="-1"
    id="PresetManagerModal">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title" id="presetManagerTitle">
                    <i class="bi bi-gear-fill" aria-hidden="true"></i> {m.preset_manager_title()}
                </h5>
                <button 
                    type="button" 
                    class="btn-close" 
                    onclick={hideModal}
                    aria-label={m.preset_manager_close_aria_label()}>
                </button>
            </div>
            <div class="modal-body">
                {#if loading}
                    <div class="text-center py-5">
                        <BarLoader size="60" color="#0d6efd" unit="px" duration="1s" />
                        <p class="mt-3">{m.preset_manager_loading_text()}</p>
                    </div>
                {:else}
                    <!-- Tabs -->
                    <ul class="nav nav-tabs mb-3" role="tablist">
                        {#each tabs as tab}
                            <li class="nav-item" role="presentation">
                                <button
                                    class="nav-link {activeTab === tab.id ? 'active' : ''}"
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === tab.id}
                                    onclick={() => switchTab(tab.id)}>
                                    <i class="bi {tab.icon}" aria-hidden="true"></i>
                                    {tab.label}
                                </button>
                            </li>
                        {/each}
                    </ul>

                    <!-- Content -->
                    <div class="row">
                        <!-- Preset List -->
                        <div class="col-md-4">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <h6 class="mb-0">{m.preset_manager_saved_presets_label()}</h6>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    onclick={importPresets}
                                    title={m.preset_manager_import_title()}>
                                    <i class="bi bi-upload" aria-hidden="true"></i> {m.preset_manager_import_button()}
                                </button>
                            </div>
                            {#if presetsByType.length === 0}
                                <div class="text-center text-muted py-4 border rounded">
                                    <i class="bi bi-inbox fs-1" aria-hidden="true"></i>
                                    <p class="mt-2">{m.preset_manager_no_presets_text()}</p>
                                </div>
                            {:else}
                                <select 
                                    class="form-select preset-select"
                                    size="15"
                                    bind:value={selectedPreset}
                                    >
                                    {#each presetsByType as preset}
                                        <option value={preset}>
                                            {preset.name}
                                        </option>
                                    {/each}
                                </select>
                            {/if}
                            {#if presetsByType.length > 0}
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary mt-2 w-100"
                                    onclick={exportAllPresetsOfType}>
                                    <i class="bi bi-download" aria-hidden="true"></i> {m.preset_manager_export_all_button()}
                                </button>
                            {/if}
                        </div>

                        <!-- Preset Details -->
                        <div class="col-md-8">
                            {#if selectedPreset}
                                <div class="card">
                                    <div class="card-header d-flex justify-content-between align-items-center">
                                        <h6 class="mb-0">{selectedPreset.name}</h6>
                                        <div>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-primary me-2"
                                                onclick={exportSelectedPreset}
                                                title={m.preset_manager_export_title()}>
                                                <i class="bi bi-download" aria-hidden="true"></i> {m.preset_manager_export_button()}
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-outline-danger"
                                                onclick={deleteSelectedPreset}
                                                title={m.preset_manager_delete_title()}>
                                                <i class="bi bi-trash" aria-hidden="true"></i> {m.preset_manager_delete_button()}
                                            </button>
                                        </div>
                                    </div>
                                    <div class="card-body">
                                        {#if selectedPreset.description}
                                            <p class="text-muted">{selectedPreset.description}</p>
                                        {/if}
                                        <hr />
                                        <h6>{m.preset_manager_configuration_label()}</h6>
                                        <pre class="bg-light p-3 rounded" style="max-height: 300px; overflow-y: auto;"><code>{JSON.stringify(selectedPreset.config, null, 2)}</code></pre>
                                        <div class="mt-3">
                                            <small class="text-muted">
                                                <strong>{m.preset_manager_type_label()}</strong> {selectedPreset.type}<br />
                                                <strong>{m.preset_manager_created_label()}</strong> {new Date(selectedPreset.createdAt).toLocaleString()}<br />
                                                <strong>{m.preset_manager_updated_label()}</strong> {new Date(selectedPreset.updatedAt).toLocaleString()}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            {:else}
                                <div class="text-center text-muted py-5">
                                    <i class="bi bi-hand-index fs-1" aria-hidden="true"></i>
                                    <p class="mt-2">{m.preset_manager_select_preset_text()}</p>
                                </div>
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" onclick={hideModal}>
                    {m.preset_manager_close_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
    .preset-select {
        max-height: 400px;
        overflow-y: auto;
        font-size: 0.95rem;
    }
    
    .preset-select option {
        padding: 0.5rem;
    }
    
    pre code {
        font-size: 0.85rem;
    }
</style>
